import { clsx } from "clsx"
import React from "react"
import { measureText } from "../../hooks/use-measure-text"
import { getBoundaryValueFromString, hasBoundaryThumbnail } from "../../models/boundaries/boundary-types"
import { IAttribute } from "../../models/data/attribute"
import { kDefaultNumPrecision } from "../../models/data/attribute-types"
import { defaultPointColor, parseColor } from "../../utilities/color-utils"
import { isStdISODateString } from "../../utilities/date-iso-utils"
import { parseDate } from "../../utilities/date-parser"
import { formatDate } from "../../utilities/date-utils"
import { gLocale } from "../../utilities/translation/locale"
import { t } from "../../utilities/translation/translate"
import {
  kCaseTableBodyFont, kCaseTableHeaderFont, kDefaultRowHeight,
  kMaxAutoColumnWidth, kMinAutoColumnWidth, lineCountFromRowHeight
} from "../case-table/case-table-types"
import {CheckboxCell, isBoolean} from "./checkbox-cell"

export const UNICODE_MINUS = "\u2212"

// cache number formatters so we don't have to generate them on every render
interface IFormatterPair {
  numberFormat: Intl.NumberFormat
  formatFn: (n: number) => string
}
const formatters = new Map<string, IFormatterPair>()

// expects d3-format style format strings like ",.2~f"
export const getNumFormatter = (formatStr: string) => {
  let formatterPair = formatters.get(formatStr)
  if (!formatterPair) {
    const match = formatStr.match(/,?\.([0-9]+)~f/)
    const precision = match?.[1] ? parseInt(match[1], 10) : kDefaultNumPrecision
    const useGrouping = formatStr.startsWith(",")
    const numberFormat = new Intl.NumberFormat(gLocale.current, { maximumFractionDigits: precision, useGrouping })
    const formatFn = (n: number) => {
      // NaN is rendered as empty (V2 parity). The literal token "NaN" is meaningless to most
      // users; an empty cell communicates "no value" more clearly. Infinity is left to Intl.
      if (Number.isNaN(n)) return ""
      const str = numberFormat.format(n)
      const _match = /^-(0\.?0*)$/.exec(str)
      if (_match?.[1]) return _match[1] // handle negative zero
      return str.startsWith("-") ? UNICODE_MINUS + str.slice(1) : str
    }
    formatterPair = { numberFormat, formatFn }
    formatters.set(formatStr, formatterPair)
  }
  return formatterPair.formatFn
}

export function getNumFormatterForAttribute(attr?: IAttribute) {
  // uses d3-format style format strings like ",.2~f"
  // Suppress grouping separators for year types and categorical attributes
  const grouping = attr?.isInferredYearType?.() || attr?.userType === "categorical" ? "" : ","
  const formatStr = `${grouping}.${attr?.numPrecision ?? kDefaultNumPrecision}~f`
  return getNumFormatter(formatStr)
}

export interface IRenderAttributeValueOptions {
  caseId?: string
  key?: number
  rowHeight?: number
  showUnits?: boolean
}

export function renderAttributeValue(str = "", num = NaN, attr?: IAttribute, options?: IRenderAttributeValueOptions) {
  const { type, userType, datePrecision, id: attrId } = attr || {}
  const { caseId, key, rowHeight = kDefaultRowHeight, showUnits } = options || {}
  let formatClass = ""
  // https://css-tricks.com/almanac/properties/l/line-clamp/
  const lineClamp = rowHeight > kDefaultRowHeight
                      ? lineCountFromRowHeight(rowHeight)
                      : 0
  const textCellStyle: React.CSSProperties = { WebkitLineClamp: lineClamp }
  if (lineClamp === 0) {
    textCellStyle.whiteSpace = "nowrap"
  }

  // boundary thumbnails
  let boundary: Maybe<object>
  if (type === "boundary" || (type === "categorical" && (boundary = getBoundaryValueFromString(str)))) {
    const boundaryObject = boundary || getBoundaryValueFromString(str)
    if (hasBoundaryThumbnail(boundaryObject)) {
      const thumb = boundaryObject?.properties?.THUMB
      if (thumb) {
        const altText = boundaryObject?.properties?.NAME ?? str
        return {
          value: altText,
          content: (
            <span className="cell-boundary-thumb">
              <img src={thumb} alt={altText} className="cell-boundary-thumb-interior" />
            </span>
          )
        }
      }
    }
  }

  // qualitative bars
  if (type === "qualitative") {
    if (str) {
      const pctStr = num >= 0 ? str : "0"
      return {
        value: str,
        content: <span className="cell-qualitative-backing" key={key}
                  role="img" aria-label={t("V3.CaseTable.qualitativeBarAriaLabel", { vars: [str] })}>
                  <span className="cell-qualitative-bar"
                        style={{background: defaultPointColor, width: `${pctStr}%`}}/>
                 </span>
      }
    }
  }

  // colors
  const color = type === "color" || !userType ? parseColor(str, { colorNames: type === "color" }) : ""
  if (color) {
    return {
      value: color,
      content: (
        <div className="cell-color-swatch" key={key}
          role="img" aria-label={t("V3.CaseTable.colorSwatchCellAriaLabel", { vars: [color] })}>
          <div className="cell-color-swatch-interior" style={{ background: color }} />
        </div>
      )
    }
  }

  // checkboxes
  if (userType === "checkbox" && isBoolean(str)) {
    return {
      value: str,
      content: <CheckboxCell caseId={caseId ?? ""} attrId={attrId ?? ""} />
    }
  }

  // numbers — finite values for any attribute, plus NaN for numeric and formula attributes when
  // str === "NaN" (i.e., the value came from computation, stored via importValueToString(NaN)).
  // The str check protects non-numeric strings whose Number() coerces to NaN: case-card summaries
  // like "3-80", user-typed invalid input like "abc", and formula results that return strings.
  // Formula attributes are admitted because their type may be unset when every value is NaN
  // (e.g., a "0/0" formula). Infinity falls through to the default text branch so the literal
  // "Infinity" remains visible — still potentially meaningful, unlike NaN.
  const isComputedNaN = (type === "numeric" || attr?.hasFormula) && str === "NaN" && Number.isNaN(num)
  if (isFinite(num) || isComputedNaN) {
    const formatter = getNumFormatterForAttribute(attr)
    if (formatter) {
      const formatted = formatter(num)
      // Suppress units when the formatted value is empty (NaN renders as ""); otherwise an
      // empty cell would surface as " kg".
      str = formatted && showUnits ? `${formatted} ${attr?.units}` : formatted
      formatClass = "numeric-format"
    }
  }

  // Dates
  // Values are formatted as dates (and right-aligned) when:
  // 1. The value is a standard ISO string produced by Date.toISOString() (e.g., from date formulas)
  // 2. The user explicitly sets the attribute type to "date"
  // 3. The attribute's values are all inferred to be dates
  // Note: CODAP v3 stores all case values as strings, so we check string patterns rather than Date instances.
  const isInferredDate = attr?.isInferredDateType?.()
  if (isStdISODateString(str) || (userType === "date" || isInferredDate) && str !== "") {
    const date = parseDate(str, true)
    if (date) {
      const formattedDate = formatDate(date, datePrecision)
      return {
        value: formattedDate || str,
        content:  <div className="cell-content numeric-format" key={key} style={textCellStyle}>
                    {formattedDate || `"${str}"`}
                  </div>
      }
    } else {
      // If the date is not valid, wrap it in quotes (CODAP V2 behavior).
      str = `"${str}"`
    }
  }

  return {
    value: str,
    content:  <div className={clsx("cell-content", formatClass)} key={key} style={textCellStyle}>
                {str}
              </div>
  }
}

export const findLongestContentWidth = (attr: IAttribute) => {
  // We take in to account that attribute names can appear over two lines and elided
  // when calculating their widths
  const headerWidth = measureText(`${attr.name}${attr.units ? `_(${attr.units})` : ''}`, kCaseTableHeaderFont)
  let longestWidth = Math.max(kMinAutoColumnWidth, Math.ceil(3 + (headerWidth/2)))
  for (let i = 0; i < attr.length; ++i) {
    // use the formatted attribute value in content width calculation
    const { value } = renderAttributeValue(attr.strValues[i], attr.numValues[i], attr)
    longestWidth = Math.max(longestWidth, measureText(value, kCaseTableBodyFont))
  }
  return Math.min(longestWidth, kMaxAutoColumnWidth)
}
